/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_FILTERS_SPLIT
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted filter fields from UsersFilters.tsx to reduce file size.
 */

import React from "react";
import { Row, Col, Form, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { UserFilters } from "../../../../interfaces/adminUsers";
import AdminButton from "../../../../components/admin/AdminButton";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_USER_FILTER_FIELDS
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replace small inline Button instances with AdminButton to keep filter quick
 * actions on approved tokens.
 *
 * File Size Check:
 * - Current: 134
 * - Adding: ~12
 * - Projected: ~146
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-figma-1)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/users/components/UserFilterFields.tsx
 */

interface UserFilterFieldsProps {
  params: UserFilters;
  searchValue: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  setSearchValue: (val: string) => void;
  onChange: (patch: Partial<UserFilters>) => void;
  handleFilterChange: (key: any, value: any) => void;
}

export const UserFilterFields = ({
  params,
  searchValue,
  handleSearchChange,
  handleSearchKeyPress,
  setSearchValue,
  onChange,
  handleFilterChange,
}: UserFilterFieldsProps) => {
  return (
    <>
      {/* Global Search */}
      <Col md={4}>
        <Form.Group>
          <Form.Label className="small text-muted">Search</Form.Label>
          <InputGroup>
            <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, email..."
              value={searchValue}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
            />
            {searchValue && (
              <AdminButton
                intent="secondary"
                size="sm"
                onClick={() => {
                  setSearchValue("");
                  onChange({ q: undefined, page: 1 });
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </AdminButton>
            )}
          </InputGroup>
        </Form.Group>
      </Col>

      {/* Role Filter */}
      <Col md={2}>
        <Form.Group>
          <Form.Label className="small text-muted">Role</Form.Label>
          <Form.Select value={params.role || ""} onChange={(e) => handleFilterChange("role", e.target.value)}>
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </Form.Select>
        </Form.Group>
      </Col>

      {/* Status Filter */}
      <Col md={2}>
        <Form.Group>
          <Form.Label className="small text-muted">Status</Form.Label>
          <Form.Select value={params.status || ""} onChange={(e) => handleFilterChange("status", e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </Form.Select>
        </Form.Group>
      </Col>

      {/* Verified Filter */}
      <Col md={2}>
        <Form.Group>
          <Form.Label className="small text-muted">Verified</Form.Label>
          <Form.Select value={params.verified || ""} onChange={(e) => handleFilterChange("verified", e.target.value)}>
            <option value="">All</option>
            <option value="1">Verified</option>
            <option value="0">Unverified</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  );
};

export const UserDateFilters = ({ params, handleFilterChange, handleClearFilters, onChange }: {
  params: UserFilters;
  handleFilterChange: (key: any, value: any) => void;
  handleClearFilters: () => void;
  onChange: (patch: Partial<UserFilters>) => void;
}) => {
  return (
    <Row className="g-3 mt-2">
      <Col md={3}>
        <Form.Group>
          <Form.Label className="small text-muted">Date From</Form.Label>
          <Form.Control type="date" value={params.dateFrom || ""} onChange={(e) => handleFilterChange("dateFrom", e.target.value)} />
        </Form.Group>
      </Col>
      <Col md={3}>
        <Form.Group>
          <Form.Label className="small text-muted">Date To</Form.Label>
          <Form.Control type="date" value={params.dateTo || ""} onChange={(e) => handleFilterChange("dateTo", e.target.value)} />
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group>
          <Form.Label className="small text-muted">Quick Filters</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
            <AdminButton intent="secondary" size="sm" onClick={handleClearFilters}>All</AdminButton>
            <AdminButton intent="secondary" size="sm" onClick={() => onChange({ role: "admin", page: 1 })}>Admins</AdminButton>
            <AdminButton intent="secondary" size="sm" onClick={() => onChange({ status: "suspended", page: 1 })}>Suspended</AdminButton>
            <AdminButton intent="secondary" size="sm" onClick={() => onChange({ verified: "1", page: 1 })}>Verified</AdminButton>
          </div>
        </Form.Group>
      </Col>
    </Row>
  );
};
